import { useEffect, useState } from 'react';
import { LESSONS, getLesson, nextLessonId } from '../game/lessons.js';
import { RANKS, SUITS, hiLoValue } from '../game/engine.js';
import { basicStrategyAction, reasonFor } from '../game/basicStrategy.js';
import { Card, Hand } from '../components/Card.jsx';

export function LessonsMode({ progress, setProgress, onJumpTo }) {
  const [activeId, setActiveId] = useState(null);

  if (!activeId) {
    return (
      <div className="px-4 py-6 max-w-3xl mx-auto flex flex-col gap-5">
        <div>
          <h2 className="font-display text-3xl font-bold mb-2">Lessons</h2>
          <p className="text-white/70 text-sm">Work through these in order. Each one is short. The trainer assumes the previous lesson stuck.</p>
        </div>

        <div className="flex flex-col gap-2">
          {LESSONS.map((l, i) => {
            const done = progress.completed?.includes(l.id);
            const locked = i > 0 && !progress.completed?.includes(LESSONS[i - 1].id);
            return (
              <button
                key={l.id}
                onClick={() => !locked && setActiveId(l.id)}
                disabled={locked}
                className={`text-left p-4 rounded-lg border transition ${
                  done ? 'bg-emerald-900/30 border-emerald-400/40' :
                  locked ? 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed' :
                  'bg-white/5 border-white/20 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-base flex items-center gap-2">
                    <span className="text-white/40 text-sm">{i + 1}.</span>
                    {l.title}
                    {done && <span className="text-emerald-400 text-sm">✓</span>}
                    {locked && <span className="text-white/40 text-xs">🔒</span>}
                  </div>
                </div>
                <p className="text-sm text-white/60">{l.blurb}</p>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const lesson = getLesson(activeId);
  return (
    <LessonView
      lesson={lesson}
      onComplete={() => {
        setProgress((p) => ({
          ...p,
          completed: Array.from(new Set([...(p.completed || []), lesson.id])),
        }));
      }}
      onNext={(nextId) => {
        if (nextId) setActiveId(nextId);
        else {
          setActiveId(null);
          onJumpTo?.('table');
        }
      }}
      onBack={() => setActiveId(null)}
    />
  );
}

function LessonView({ lesson, onComplete, onNext, onBack }) {
  return (
    <div className="px-4 py-6 max-w-3xl mx-auto flex flex-col gap-5">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm self-start">← All lessons</button>
      <h2 className="font-display text-3xl font-bold">{lesson.title}</h2>
      <p className="text-white/70 italic">{lesson.blurb}</p>

      <div className="flex flex-col gap-6">
        {lesson.blocks.map((b, i) => <Block key={i} block={b} onCta={(nextId) => {
          onComplete();
          onNext(nextId ?? nextLessonId(lesson.id));
        }} />)}
      </div>
    </div>
  );
}

function Block({ block, onCta }) {
  if (block.type === 'prose') {
    return <Prose text={block.text} />;
  }
  if (block.type === 'cta') {
    return (
      <button onClick={() => onCta(block.next)}
        className="self-start px-6 py-3 bg-chip text-black font-bold rounded-lg hover:brightness-110 transition">
        {block.label}
      </button>
    );
  }
  if (block.type === 'demo') {
    if (block.kind === 'strategy-card-flip') return <StrategyCardFlipDemo />;
    if (block.kind === 'count-flash') return <CountFlashDemo />;
    if (block.kind === 'true-count-demo') return <TrueCountDemo />;
  }
  return null;
}

// Renders simple paragraph text. Bold via **x**, headings via leading bullets/numbers handled naturally.
function Prose({ text }) {
  const paras = text.split(/\n\n+/);
  return (
    <div className="space-y-4 text-white/85 leading-relaxed">
      {paras.map((p, i) => <p key={i} dangerouslySetInnerHTML={{ __html: renderInline(p) }} />)}
    </div>
  );
}

function renderInline(s) {
  // Bold **x**
  let out = s.replace(/\*\*(.+?)\*\*/g, '<b class="text-white">$1</b>');
  // Inline code `x`
  out = out.replace(/`(.+?)`/g, '<code class="bg-black/30 px-1.5 py-0.5 rounded text-chip text-[0.95em]">$1</code>');
  // Newlines within paragraph
  out = out.replace(/\n/g, '<br/>');
  return out;
}

// === Demos ===

function rand(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function randomCard() { return { rank: rand(RANKS), suit: rand(SUITS) }; }

function StrategyCardFlipDemo() {
  const [scenario, setScenario] = useState(() => ({ player: [randomCard(), randomCard()], up: randomCard() }));
  const [revealed, setRevealed] = useState(false);
  const optimal = basicStrategyAction(scenario.player, scenario.up.rank, { canDouble: true, canSurrender: true, canSplit: false, das: true });
  const reason = reasonFor(scenario.player, scenario.up.rank, optimal);
  return (
    <div className="bg-black/20 rounded-lg p-5 border border-white/10 flex flex-col items-center gap-4">
      <div className="text-xs uppercase tracking-widest text-white/50">Try one</div>
      <div className="flex items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-white/50 uppercase">Dealer</span>
          <Card card={scenario.up} size="sm" />
        </div>
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-white/50 uppercase">You</span>
          <Hand cards={scenario.player} size="sm" />
        </div>
      </div>
      {revealed ? (
        <div className="text-center">
          <div className="text-chip font-bold text-lg uppercase">{optimal}</div>
          <p className="text-sm text-white/80 mt-1 max-w-md">{reason}</p>
        </div>
      ) : (
        <button onClick={() => setRevealed(true)} className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded text-sm border border-white/20">
          What's the optimal play?
        </button>
      )}
      <button onClick={() => { setScenario({ player: [randomCard(), randomCard()], up: randomCard() }); setRevealed(false); }}
        className="text-xs text-white/50 hover:text-white">Try another</button>
    </div>
  );
}

function CountFlashDemo() {
  const [cards, setCards] = useState(() => Array.from({ length: 5 }, randomCard));
  const [revealed, setRevealed] = useState(false);
  const total = cards.reduce((acc, c) => acc + hiLoValue(c.rank), 0);
  return (
    <div className="bg-black/20 rounded-lg p-5 border border-white/10 flex flex-col items-center gap-3">
      <div className="text-xs uppercase tracking-widest text-white/50">Count these</div>
      <div className="flex gap-2 flex-wrap justify-center">
        {cards.map((c, i) => (
          <div key={i} className="flex flex-col items-center">
            <Card card={c} size="sm" />
            {revealed && (
              <span className={`text-xs font-bold mt-1 ${hiLoValue(c.rank) > 0 ? 'text-emerald-400' : hiLoValue(c.rank) < 0 ? 'text-rose-400' : 'text-white/50'}`}>
                {hiLoValue(c.rank) > 0 ? '+1' : hiLoValue(c.rank)}
              </span>
            )}
          </div>
        ))}
      </div>
      {revealed ? (
        <div className="text-center">
          <div className="text-2xl font-bold text-chip">Running count: {total > 0 ? `+${total}` : total}</div>
        </div>
      ) : (
        <button onClick={() => setRevealed(true)} className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded text-sm border border-white/20">
          Reveal the count
        </button>
      )}
      <button onClick={() => { setCards(Array.from({ length: 5 }, randomCard)); setRevealed(false); }}
        className="text-xs text-white/50 hover:text-white">New cards</button>
    </div>
  );
}

function TrueCountDemo() {
  const [rc, setRc] = useState(6);
  const [decks, setDecks] = useState(2);
  const tc = (rc / Math.max(0.25, decks));
  return (
    <div className="bg-black/20 rounded-lg p-5 border border-white/10 flex flex-col gap-3">
      <div className="text-xs uppercase tracking-widest text-white/50 text-center">Try it</div>
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-white/70">Running count</span>
          <input type="number" value={rc} onChange={(e) => setRc(parseInt(e.target.value || 0, 10))}
            className="px-3 py-2 rounded bg-white/10 border border-white/20 text-center text-lg" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm text-white/70">Decks remaining</span>
          <input type="number" step="0.5" value={decks} onChange={(e) => setDecks(parseFloat(e.target.value || 1))}
            className="px-3 py-2 rounded bg-white/10 border border-white/20 text-center text-lg" />
        </label>
      </div>
      <div className="text-center mt-2">
        <div className="text-3xl font-bold text-chip">True count: {tc >= 0 ? '+' : ''}{tc.toFixed(1)}</div>
        <div className="text-sm text-white/60 mt-1">
          {tc >= 3 ? "You'd take insurance here. Bet near the top of your spread." :
           tc >= 1 ? 'Slight edge — bet up.' :
           tc <= -1 ? 'Negative — minimum bet.' :
           'Neutral — bet base unit.'}
        </div>
      </div>
    </div>
  );
}
